import type { LevelId } from '../data/ids';

/** Jump grades of El Salto (GDD §11). Same shape as the save file (src/save/schema.ts maps it). */
export type JumpGrade = 'PERFECTO' | 'EXCELENTE' | 'BUENO' | 'REGULAR' | 'FORZOSO';

/** Best result of a completed level; bestJump is null in levels without El Salto (GDD §6.4). */
export interface LevelRecord {
  bestScore: number;
  bestJump: JumpGrade | null;
  cleanRun: boolean;
  /** The best score was made with the assist mode on (GDD §13.6, #138): shown as ASISTIDO and never replaces an unassisted one. */
  assisted: boolean;
}

export type RunOutcome = 'EN_CURSO' | 'OPERACION_FALLIDA';

/**
 * The persistent part of a game in memory: the reflection of SaveSlot.current and of the unlock lists of
 * GDD §6.4, with the same names and shapes so that the save (#46, #47) maps it directly. Lives, score and
 * dollars live in the core world; this keeps what survives a level.
 */
export interface RunState {
  levelId: LevelId;
  checkpointId: string | null;
  /** Score when the level started, restored on game over (GDD §6.2, rule 6). */
  scoreAtLevelStart: number;
  unlockedLevels: LevelId[];
  completedLevels: Partial<Record<LevelId, LevelRecord>>;
  /** The test level OP-000: flown like any level but outside the campaign, never saved (GDD §16.4). */
  test: boolean;
  /** OPERACION_FALLIDA after a game over; #50 turns it into the formal level state. */
  outcome: RunOutcome;
  /** No life lost in the current attempt at the level: worth the clean-run bonus (GDD §12). */
  cleanRun: boolean;
  /** The level was loaded with the assist mode on (GDD §13.6, #138): the setting is read at load, never mid-level. */
  assisted: boolean;
}

export function createRunState(levelId: LevelId, overrides: Partial<RunState> = {}): RunState {
  return {
    levelId,
    checkpointId: null,
    scoreAtLevelStart: 0,
    unlockedLevels: ['1-1'],
    completedLevels: {},
    outcome: 'EN_CURSO',
    test: false,
    cleanRun: true,
    assisted: false,
    ...overrides,
  };
}
